import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Key, ArrowsClockwise, Copy, Trash, Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * Gestor de API key externa para integraciones (videoperitaje propio, etc).
 * - Lista las keys (sólo prefijo + metadatos).
 * - Rotar: revoca la anterior y devuelve una nueva. Se muestra UNA vez.
 * - Revocar: invalida la key actual sin generar una nueva.
 */
export default function ExternalApiKeyManager() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [newKey, setNewKey] = useState(null); // string visible UNA vez
  const [copied, setCopied] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookToken, setWebhookToken] = useState("");
  const [savingWebhook, setSavingWebhook] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [k, w] = await Promise.all([
        api.get("/settings/external-api-key"),
        api.get("/settings/external-api-key/webhook"),
      ]);
      setKeys(k.data?.keys || []);
      setWebhookUrl(w.data?.webhook_url || "");
      setWebhookToken(w.data?.outbound_token || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando API keys");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rotate = async () => {
    if (!window.confirm("Esto generará una nueva API key y revocará la anterior. ¿Continuar?")) return;
    setRotating(true);
    setCopied(false);
    try {
      const r = await api.post("/settings/external-api-key/rotate", { name: "Videoperitaje propio" });
      setNewKey(r.data?.key);
      toast.success("Nueva API key generada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando key");
    } finally {
      setRotating(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("¿Revocar esta key? El sistema externo dejará de funcionar inmediatamente.")) return;
    try {
      await api.delete(`/settings/external-api-key/${id}`);
      toast.success("Key revocada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error revocando");
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(newKey);
      setCopied(true);
      toast.success("API key copiada");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const saveWebhook = async () => {
    setSavingWebhook(true);
    try {
      await api.post("/settings/external-api-key/webhook", {
        webhook_url: webhookUrl,
        outbound_token: webhookToken,
      });
      toast.success("Webhook guardado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSavingWebhook(false);
    }
  };

  const activeKey = keys.find((k) => !k.revoked);

  return (
    <section className="bg-white border border-[#E8EAED] rounded-[10px]" data-testid="external-api-key-section">
      <div className="px-5 py-4 border-b border-[#E8EAED] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="chip-icon chip-icon-amber"><Key className="w-4 h-4" weight="regular" /></span>
          <div className="min-w-0">
            <h2 className="font-display font-semibold text-[15px] tracking-tight">Integraciones · API Key externa</h2>
            <p className="text-xs text-muted mt-0.5">Usa esta key para que tu sistema de videoperitación llame a Ekspertium.</p>
          </div>
        </div>
        <button
          onClick={rotate}
          disabled={rotating}
          data-testid="rotate-api-key"
          className="text-xs font-semibold inline-flex items-center gap-1.5 bg-brand text-white px-3.5 py-1.5 rounded-md hover:opacity-90 disabled:opacity-50 transition"
        >
          <ArrowsClockwise className="w-3.5 h-3.5" weight="regular" /> {activeKey ? "Rotar" : "Generar"}
        </button>
      </div>

      <div className="p-5 space-y-4">
        {/* Key recién generada */}
        {newKey && (
          <div className="bg-[#FDF0DB] border border-[#F5D9A6] rounded-[10px] p-4 text-[12px] space-y-2">
            <div className="font-semibold text-[#B45309] flex items-center gap-1.5">
              <Key className="w-3.5 h-3.5" weight="fill" /> Tu nueva API key (visible solo esta vez)
            </div>
            <div className="bg-white border border-[#F5D9A6] rounded-md px-3 py-2 flex items-center gap-2">
              <code className="font-mono text-[11.5px] break-all flex-1" data-testid="generated-api-key">{newKey}</code>
              <button onClick={copyKey} className="text-[#B45309] hover:text-[#92400e] p-1" title="Copiar">
                {copied ? <Check className="w-4 h-4" weight="bold" /> : <Copy className="w-4 h-4" weight="regular" />}
              </button>
            </div>
            <div className="text-[#B45309]">
              Guárdala YA en un sitio seguro. Después solo verás el prefijo. Si la pierdes tendrás que rotar y reconfigurar tu sistema.
            </div>
            <button onClick={() => setNewKey(null)} className="text-[#B45309] underline text-[11px]">Ya la he guardado</button>
          </div>
        )}

        {/* Tabla de keys */}
        <div className="border border-[#E8EAED] rounded-[10px] overflow-hidden">
          <table className="table-modern" data-testid="api-keys-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Prefijo</th>
                <th>Creada</th>
                <th>Último uso</th>
                <th className="!text-right">Llamadas</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center text-muted py-6">Cargando…</td></tr>
              )}
              {!loading && keys.length === 0 && (
                <tr><td colSpan={7} className="text-center text-muted py-6">
                  No hay ninguna API key. Pulsa "Generar" para crear la primera.
                </td></tr>
              )}
              {keys.map((k) => (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td className="font-mono text-[12px]">{k.prefix}</td>
                  <td className="text-[12px] tabular-nums">{k.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="text-[12px] tabular-nums text-muted">
                    {k.last_used_at ? k.last_used_at.slice(0, 16).replace("T", " ") : "—"}
                  </td>
                  <td className="cell-num">{k.calls_count || 0}</td>
                  <td>
                    {k.revoked ? (
                      <span className="text-[10.5px] px-2 py-0.5 rounded-full border bg-[#FEE2E2] border-[#FCA5A5] text-[#991B1B]">Revocada</span>
                    ) : (
                      <span className="text-[10.5px] px-2 py-0.5 rounded-full border bg-[#DCFCE7] border-[#86EFAC] text-[#166534]">Activa</span>
                    )}
                  </td>
                  <td className="!text-right">
                    {!k.revoked && (
                      <button
                        onClick={() => revoke(k.id)}
                        className="text-[#991B1B] hover:bg-[#FEE2E2] p-1.5 rounded transition"
                        title="Revocar"
                        data-testid={`revoke-${k.id}`}
                      >
                        <Trash className="w-3.5 h-3.5" weight="regular" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Webhook saliente */}
        <div className="border border-[#E8EAED] rounded-[10px] p-4 space-y-3">
          <div className="font-semibold text-[13px]">Webhook saliente (opcional)</div>
          <p className="text-[12px] text-muted">
            Cuando se cree una videoperitación en Ekspertium con proveedor "propio", se hará un POST a esta URL con los datos del expediente. Tu sistema validará el token compartido.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-muted font-semibold mb-1">URL del webhook</label>
              <input
                type="url"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://tu-sistema.example.com/api/sessions/new"
                data-testid="webhook-url-input"
                className="!text-[12.5px]"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wide text-muted font-semibold mb-1">Token compartido (lo enviamos en header)</label>
              <input
                type="text"
                value={webhookToken}
                onChange={(e) => setWebhookToken(e.target.value)}
                placeholder="secreto-que-tu-sistema-valida"
                data-testid="webhook-token-input"
                className="!text-[12.5px] font-mono"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={saveWebhook}
              disabled={savingWebhook}
              data-testid="save-webhook"
              className="text-xs font-semibold bg-foreground text-white px-3.5 py-1.5 rounded-md hover:opacity-90 disabled:opacity-50 transition"
            >
              Guardar webhook
            </button>
          </div>
        </div>

        {/* Documentación inline */}
        <details className="border border-[#E8EAED] rounded-[10px] p-4 text-[12px]">
          <summary className="cursor-pointer font-semibold text-[13px]">Documentación rápida de los endpoints</summary>
          <div className="mt-3 space-y-2 text-muted">
            <p>
              Pasa la API key en el header <code className="bg-[#FBFBFC] px-1.5 py-0.5 rounded border border-[#E8EAED]">X-API-Key</code> en cada petición.
            </p>
            <div className="bg-[#FBFBFC] border border-[#E8EAED] rounded-md p-3 font-mono text-[11.5px] overflow-x-auto whitespace-pre">{`# 1) Obtener datos del expediente
GET  /api/external/videoperitaje/expedientes/{id|nº siniestro}

# 2) Actualizar estado de la sesión
POST /api/external/videoperitaje/sessions/{ref}/state
{ "status": "in_progress" | "completed" | "failed" | "cancelled",
  "notes": "...", "meta": { ... } }

# 3) Cerrar con resultado (descargamos tus URLs y las adjuntamos al expediente)
POST /api/external/videoperitaje/sessions/{ref}/result
{ "video_url": "https://...",
  "notes": "...", "transcript": "...",
  "media": [
    { "url": "https://...", "kind": "photo"|"video", "name": "..." }
  ] }`}</div>
          </div>
        </details>
      </div>
    </section>
  );
}
